/**
 * concierge-logic.js
 * AIコンシェルジュ — メインロジック（完全版）
 */

class AIConcierge {
    constructor() {
        this.config = {
            wsUrl:             'wss://sasagawa.tokyo/rag/',
            reconnectInterval: 3000,
            pingInterval:      15000,
            // ── ハイブリッド検索設定 ──
            hybridSearch:      true,          // false にすれば従来のサーバー検索に戻る
            topK:              3,             // config.py の top_k と揃える
        };

        // 状態管理
        this.ws           = null;
        this.sessionId    = null;
        this.isStreaming   = false;
        this.streamBubble = null;
        this.elapsedSec   = 0;
        this.elapsedTimer = null;
        this.pingTimer    = null;
        this.reconnTimer  = null;

        // ── ハイブリッド検索 ──
        this.search       = null;             // ConciergeSearch インスタンス
        this.searchReady  = false;            // ブラウザ検索が使えるか

        // DOM要素
        this.nodes = {
            chatMain:    document.getElementById('chat-messages'),
            userInput:   document.getElementById('user-input'),
            sendBtn:     document.getElementById('send-btn'),
            statusText:  document.getElementById('status-text'),
            statusBadge: document.getElementById('status-badge'),
            elapsedTime: document.getElementById('elapsed-time'),
            systemStatus:document.getElementById('system-status'),
            loadingBar:  document.getElementById('loading-bar'),
        };

        this.init();
    }

    // ══════════════════════════════════════════
    // 初期化
    // ══════════════════════════════════════════
    init() {
        this.bindEvents();
        this.connect();
        // ハイブリッド検索を裏で準備（UIをブロックしない）
        if (this.config.hybridSearch) {
            this.initSearch();
        }
    }

    // ── ブラウザ検索の初期化（失敗してもサーバー検索で動く）──
    async initSearch() {
        if (typeof window.ConciergeSearch !== 'function') {
            console.warn('[concierge] ConciergeSearch 未読込 → サーバー検索を使用');
            return;
        }
        this.search = new window.ConciergeSearch({ topK: this.config.topK });
        const ok = await this.search.init();
        this.searchReady = ok;
        if (ok) {
            console.log('[concierge] ハイブリッド検索 有効');
        } else {
            console.log('[concierge] ハイブリッド検索 無効 → サーバー検索にフォールバック');
        }
    }

    // ══════════════════════════════════════════
    // WebSocket 接続
    // ══════════════════════════════════════════
    connect() {
        this.updateConnectionStatus('pending');
        this.ws = new WebSocket(this.config.wsUrl);

        this.ws.onopen = () => {
            if (this.reconnTimer) {
                clearTimeout(this.reconnTimer);
                this.reconnTimer = null;
            }
            this.startPing();
        };

        this.ws.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                this.handleServerMessage(data);
            } catch (e) {
                console.error('JSON parse error:', e);
            }
        };

        this.ws.onclose = () => {
            this.updateConnectionStatus('offline');
            this.stopPing();
            this.finishStream();
            this.reconnTimer = setTimeout(
                () => this.connect(), this.config.reconnectInterval
            );
        };

        this.ws.onerror = () => {
            this.updateSystemStatus('接続エラー');
        };
    }

    // ══════════════════════════════════════════
    // サーバーメッセージハンドラ
    // ══════════════════════════════════════════
    handleServerMessage(data) {
        switch (data.type) {

            case 'connected':
                this.sessionId = data.session_id;
                this.updateConnectionStatus('online');
                // キュー待ち件数の表示
                if (data.queue_length > 0) {
                    this.updateSystemStatus(`⏳ ${data.queue_msg}`);
                    this.showSystemMessage(`リクエスト待ち ${data.queue_length} 件あります`);
                } else {
                    this.updateSystemStatus('Ready');
                }
                break;

            case 'status':
                this.updateSystemStatus(data.content);
                // 推論開始でタイマースタート
                if (data.content.includes('回答を生成中')) {
                    this.startTimer();
                }
                break;

            case 'context_preview':
                this.showContextPreview(data.content);
                break;

            case 'token':
                this.appendToken(data.content);
                break;

            case 'answer':
                // content がある場合はバブルとして追加（scrape完了通知など）
                if (data.content && !this.isStreaming) {
                    this.addMessage('ai', data.content);
                }
                this.finishStream(data.sources || []);
                break;

            case 'error':
                this.finishStream();
                this.addMessage('ai', `⚠️ ${data.content}`, true);
                this.updateSystemStatus('エラーが発生しました');
                break;
        }
    }

    // ══════════════════════════════════════════
    // メッセージ送信
    // ══════════════════════════════════════════
    async sendMessage() {
        const text = this.nodes.userInput.value.trim();
        if (!text || !this.ws || this.ws.readyState !== WebSocket.OPEN) return;
        if (this.isStreaming) return;

        this.addMessage('user', text);
        this.nodes.userInput.value = '';
        this.autoResize(this.nodes.userInput);
        this.setInputEnabled(false);
        this.nodes.loadingBar.classList.remove('hidden');
        this.updateSystemStatus('処理中...');

        // ── ハイブリッド: ブラウザで検索できるなら chunk_id を送る ──
        if (this.searchReady && this.search) {
            try {
                this.updateSystemStatus('🔍 検索中...');
                const hits = await this.search.search(text, this.config.topK);
                if (hits && hits.length > 0) {
                    // 検索できた文書をブラウザ側で先に表示（体感速度UP）
                    this.showContextPreview(hits.map(h => ({
                        title: h.title, url: h.url, preview: '',
                    })));
                    // VPSには chunk_id だけ送る。本文はVPSがDBから引く。
                    this.ws.send(JSON.stringify({
                        action:    'generate',
                        question:  text,
                        chunk_ids: hits.map(h => h.id),
                    }));
                    return;
                }
                // ヒット0件 → 該当なしとして generate（contexts空）を送る
                this.ws.send(JSON.stringify({
                    action:    'generate',
                    question:  text,
                    chunk_ids: [],
                    uncovered: true,
                }));
                return;
            } catch (e) {
                console.warn('[concierge] ブラウザ検索失敗 → サーバー検索へ:', e);
                // フォールバックへ落ちる
            }
        }

        // ── フォールバック: 従来のサーバー検索 ──
        this.ws.send(JSON.stringify({ action: 'query', question: text }));
    }

    // ══════════════════════════════════════════
    // ストリーミング
    // ══════════════════════════════════════════
    appendToken(token) {
        if (!this.isStreaming) {
            this.isStreaming   = true;
            // 新しいバブルを作成し直接参照を保持
            const div          = document.createElement('div');
            div.className      = 'message ai-message streaming';
            this.nodes.chatMain.appendChild(div);
            this.streamBubble  = div;
        }
        this.streamBubble.textContent += token;
        this.nodes.chatMain.scrollTop  = this.nodes.chatMain.scrollHeight;
    }

    finishStream(sources = []) {
        this.stopTimer();
        this.nodes.loadingBar.classList.add('hidden');

        if (this.streamBubble) {
            // ストリーミングカーソルを削除
            this.streamBubble.classList.remove('streaming');

            // 参照元の追加
            if (sources.length > 0) {
                const srcDiv       = document.createElement('div');
                srcDiv.className   = 'sources-info';
                srcDiv.innerHTML   =
                    '<strong>参照元：</strong><br>' +
                    sources.map(u =>
                        `<a href="${this.escHtml(u)}" target="_blank" rel="noopener">${this.escHtml(u)}</a>`
                    ).join('<br>');
                this.streamBubble.appendChild(srcDiv);
            }
            this.streamBubble = null;
        }

        this.isStreaming = false;
        this.setInputEnabled(true);
        this.updateSystemStatus('Ready');
        this.nodes.chatMain.scrollTop = this.nodes.chatMain.scrollHeight;
    }

    // ══════════════════════════════════════════
    // メッセージバブル追加
    // ══════════════════════════════════════════
    addMessage(type, text, isError = false) {
        const div       = document.createElement('div');
        div.className   = `message ${type}-message`;
        if (isError) div.style.cssText = 'background:#fff0f0;color:#c0392b;';
        div.textContent = text;
        this.nodes.chatMain.appendChild(div);
        this.nodes.chatMain.scrollTop = this.nodes.chatMain.scrollHeight;
        return div;
    }

    // システムメッセージ（中央寄せの小さい通知）
    showSystemMessage(text) {
        const div     = document.createElement('div');
        div.className = 'system-message';
        div.textContent = text;
        this.nodes.chatMain.appendChild(div);
        this.nodes.chatMain.scrollTop = this.nodes.chatMain.scrollHeight;
    }

    // 参照文書プレビュー
    showContextPreview(contexts) {
        if (!contexts || contexts.length === 0) return;
        const div     = document.createElement('div');
        div.className = 'context-preview';
        div.innerHTML =
            '<div class="preview-label">📄 参照文書</div>' +
            contexts.map(c =>
                `<div class="preview-item">
                    <a href="${this.escHtml(c.url)}" target="_blank" rel="noopener">${this.escHtml(c.title)}</a>
                    <span class="preview-text"> — ${this.escHtml(c.preview)}</span>
                </div>`
            ).join('');
        this.nodes.chatMain.appendChild(div);
        this.nodes.chatMain.scrollTop = this.nodes.chatMain.scrollHeight;
    }

    // ══════════════════════════════════════════
    // UI ユーティリティ
    // ══════════════════════════════════════════
    updateConnectionStatus(status) {
        const b = this.nodes.statusBadge;
        const t = this.nodes.statusText;
        b.className = `status-${status}`;
        const labels = { online: 'ONLINE', pending: 'CONNECTING...', offline: 'OFFLINE' };
        t.textContent = labels[status] ?? 'OFFLINE';
        this.setInputEnabled(status === 'online' && !this.isStreaming);
    }

    updateSystemStatus(text) {
        this.nodes.systemStatus.textContent = text ?? '';
    }

    setInputEnabled(enabled, withFocus = false) {  // ← focus は明示的に指定
        this.nodes.userInput.disabled = !enabled;
        this.nodes.sendBtn.disabled   = !enabled;
        if (enabled && withFocus) this.nodes.userInput.focus();  // ← 条件追加
    }

    autoResize(el) {
        el.style.height = 'auto';
        el.style.height = Math.min(el.scrollHeight, 110) + 'px';
    }

    escHtml(s) {
        return String(s)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }

    // ══════════════════════════════════════════
    // 経過タイマー
    // ══════════════════════════════════════════
    startTimer() {
        this.stopTimer();
        this.elapsedSec = 0;
        this.nodes.elapsedTime.classList.add('running');
        this.elapsedTimer = setInterval(() => {
            this.elapsedSec++;
            const m = String(Math.floor(this.elapsedSec / 60)).padStart(2, '0');
            const s = String(this.elapsedSec % 60).padStart(2, '0');
            this.nodes.elapsedTime.textContent = `${m}:${s}`;
        }, 1000);
    }

    stopTimer() {
        if (this.elapsedTimer) {
            clearInterval(this.elapsedTimer);
            this.elapsedTimer = null;
        }
        this.nodes.elapsedTime.classList.remove('running');
        this.nodes.elapsedTime.textContent = '00:00';
    }

    // ══════════════════════════════════════════
    // Ping（接続維持）
    // ══════════════════════════════════════════
    startPing() {
        this.stopPing();
        this.pingTimer = setInterval(() => {
            if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                this.ws.send(JSON.stringify({ action: 'ping' }));
            }
        }, this.config.pingInterval);
    }

    stopPing() {
        if (this.pingTimer) {
            clearInterval(this.pingTimer);
            this.pingTimer = null;
        }
    }

    // ══════════════════════════════════════════
    // イベント登録
    // ══════════════════════════════════════════
    bindEvents() {
        this.nodes.sendBtn.onclick = () => this.sendMessage();

        this.nodes.userInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                this.sendMessage();
            }
        });

        this.nodes.userInput.addEventListener('input', () => {
            this.autoResize(this.nodes.userInput);
        });
    }
}

// ページ読み込み時に初期化
document.addEventListener('DOMContentLoaded', () => {
    window.concierge = new AIConcierge();
});
